import type { DailyChallengeIdentity } from "../config/daily-challenge-config";
import type { SeasonalChallengeIdentity } from "../config/challenge-link-config";
import type { TimedModeId } from "../config/gameplay-config";
import type { DailyChallengeDefinition } from "../config/daily-challenge-config";
import type { SeasonalChallengeDefinition } from "../config/seasonal-challenge-config";

interface ChallengeResultPresentation {
  readonly mode: TimedModeId;
  readonly resultHighlight: string;
  readonly resultsModeLabel: string;
  readonly resultsOutcome: string;
  readonly shareCallToAction: string;
  readonly shareDisplayName: string;
}

export type ChallengeResultDefinition = ChallengeResultPresentation &
  (
    | {
        readonly identity: DailyChallengeIdentity;
        readonly kind: "daily";
      }
    | {
        readonly identity: SeasonalChallengeIdentity;
        readonly kind: "seasonal";
      }
  );

export interface ChallengeDailyPort {
  getActiveChallenge(): DailyChallengeDefinition | null;
  replay(challenge: DailyChallengeDefinition): boolean;
}

export interface ChallengeSeasonalPort {
  getActiveChallenge(): SeasonalChallengeDefinition | null;
  replay(identity: SeasonalChallengeIdentity): boolean;
}

export const createDailyChallengeResultDefinition = (
  challenge: DailyChallengeDefinition,
): ChallengeResultDefinition => ({
  identity: challenge.identity,
  kind: "daily",
  mode: challenge.mode,
  resultHighlight: "Today's challenge complete",
  resultsModeLabel: `Daily Challenge · ${challenge.displayDate} · 1 minute`,
  resultsOutcome: "Daily complete!",
  shareCallToAction: "Same challenge, same day. Can you beat it?",
  shareDisplayName: `Daily Challenge · ${challenge.displayDate}`,
});

export const createSeasonalChallengeResultDefinition = (
  challenge: SeasonalChallengeDefinition,
): ChallengeResultDefinition => ({
  identity: challenge.identity,
  kind: "seasonal",
  mode: challenge.mode,
  resultHighlight: challenge.descriptor.results.highlight,
  resultsModeLabel: challenge.descriptor.results.modeLabel,
  resultsOutcome: challenge.descriptor.results.outcome,
  shareCallToAction: challenge.descriptor.share.callToAction,
  shareDisplayName: challenge.descriptor.share.displayName,
});

export class ChallengeRuntime {
  public constructor(
    private readonly daily: ChallengeDailyPort,
    private readonly seasonal: ChallengeSeasonalPort,
  ) {}

  public getActiveChallenge(): ChallengeResultDefinition | null {
    const daily = this.daily.getActiveChallenge();
    if (daily !== null) {
      return createDailyChallengeResultDefinition(daily);
    }
    const seasonal = this.seasonal.getActiveChallenge();
    return seasonal === null
      ? null
      : createSeasonalChallengeResultDefinition(seasonal);
  }

  public replay(challenge: ChallengeResultDefinition): boolean {
    if (challenge.kind === "daily") {
      const active = this.daily.getActiveChallenge();
      return active !== null && active.identity === challenge.identity
        ? this.daily.replay(active)
        : false;
    }
    return this.seasonal.replay(challenge.identity);
  }
}
